"use client";

import SeasonalImage from "@/components/SeasonalImage";
import { Gamja_Flower } from "next/font/google";
import { CalendarDays } from "lucide-react";
import { useExamDday } from "@/hooks/useExamDday";
import { ACTIVE_SEASON_SKIN } from "@/lib/season-skin";
import { cn } from "@/lib/utils";

const gamja = Gamja_Flower({
  weight: "400",
  subsets: ["latin"],
  display: "swap",
  fallback: ["serif"],
});

/**
 * 홈 대시보드 좌측 — D-day 감정 카드 (JCG-2110).
 *
 * `DdayDisplay`(모바일/클래식용 pill)와 같은 활성 회차 D-day 데이터를 공유하되,
 * 셸 좌측 사이드바에선 응원 감자 마스코트 + 웜 그라데이션 카드로 키운다.
 */
export default function DdayCard({
  variant = "sidebar",
}: {
  variant?: "sidebar" | "mobile";
}) {
  const { data } = useExamDday();

  const restCard = ACTIVE_SEASON_SKIN === "exam-rest" ? (
    <div className="hidden rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-950 season:block dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100">
      <p className={`${gamja.className} text-xl`}>정말 수고했어요 🌿</p>
      <p className="mt-1 text-sm">오늘은 나에게도 잠깐의 휴식을 선물해요.</p>
      <SeasonalImage src="/main/happy_gamza.webp" alt="" width={72} height={72} className="ml-auto h-18 w-18 object-contain" />
    </div>
  ) : null;

  if (!data || data.dday < 0) return restCard;

  const ddayText = data.dday === 0 ? "D-DAY" : `D-${data.dday}`;
  const isMobile = variant === "mobile";

  return (
    <>
      {restCard}
      <div
        className={cn(
          ACTIVE_SEASON_SKIN === "exam-rest" && "season:hidden",
          "relative overflow-hidden border border-amber-200/80 bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 text-white dark:border-amber-500/30",
          isMobile
            ? "rounded-2xl p-4 shadow-md shadow-orange-500/15"
            : "rounded-3xl p-5 shadow-lg shadow-orange-500/20",
        )}
      >
        <div aria-hidden className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/15 blur-xl" />
        <div className={cn("relative", isMobile && "pr-16")}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-white/90">
                <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                정보처리기사 실기
              </div>
              <p className="mt-1 text-xs text-white/80">{data.label}</p>
            </div>
            {!isMobile && (
              <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-bold text-white/90">
                시험 D-day
              </span>
            )}
          </div>
          <div className={cn("mt-2 flex items-end gap-2", isMobile && "mt-3")}>
            <span
              className={cn(
                "font-black leading-none tracking-tight tabular-nums",
                isMobile ? "text-[38px]" : "text-[44px]",
              )}
            >
              {ddayText}
            </span>
            <span className={`${gamja.className} mb-1 text-base text-white/95 sm:text-lg`}>
              {isMobile ? "남았어요" : "까지 같이 가요!"}
            </span>
          </div>
          <p className="mt-2 text-[11px] text-white/80">감자가 합격까지 응원할게요 🥔</p>
        </div>
        <SeasonalImage
          src="/main/happy_gamza.webp"
          alt=""
          width={72}
          height={72}
          className={cn(
            "pointer-events-none absolute right-3 object-contain drop-shadow-md",
            isMobile ? "-bottom-0.5 h-16 w-16" : "-bottom-1 h-[68px] w-[68px]",
          )}
        />
      </div>
    </>
  );
}
